import React, { useCallback, useMemo, useReducer } from "react";
import styled from "styled-components";
import CommentField from "@adsk/alloy-react-comment-field";
import Theme from "@adsk/alloy-react-theme";
import {
  createIssueCommentAction,
  getIsPostingIssueComment,
} from "@adsk/acc-issues-core/state/redux/ducks/issueComments";
import {
  useDispatch,
  useSelector,
} from "@adsk/acc-issues-core/state/redux/hooks";

import { useTranslations } from "@adsk/acc-issues-translations";
import { useUser } from "@adsk/acc-issues-core/utils/hooks/alloyHooks";
import { trackIssueAddComment } from "../../utils/analytics/issueActionsTracking";
import {
  useMentionSuggestions,
  MentionTextarea,
  MentionData,
  useMentionConfirmation,
  defaultFilter,
  MentionPopover,
} from "@adsk/acc-mention";
import { EditorState } from "draft-js";
import { ModalConfirmation } from "@adsk/alloy-react-modal";
import { MENTION_TYPES, MentionType } from "@adsk/acc-mention";
import { Company, Role, User } from "@adsk/alloy-provider";
import {
  useCompaniesWrapper,
  useRolesWrapper,
  useUsersWrapper,
} from "@adsk/acc-issues-common/hooks/data/projectMembersHooks";
import { getViewersMap } from "@adsk/acc-issues-core/state/redux/ducks/issuesViewers";
import { FetchStatus } from "@adsk/acc-issues-core/state/redux/fetchHelpers";
import { Issue, IssueViewers, IssueComment } from "@adsk/acc-issues-types";
import uniqBy from "lodash/uniqBy";
import { useWatcherPropName } from "../../utils/hooks/watchers/useWatcherPropName";

interface CommentInputProps {
  disabled?: boolean;
  focused?: boolean;
  error?: string;
}

const Textarea = styled.div`
  min-height: 70px;
  border: ${(props: CommentInputProps) =>
    props.error ? `1px solid ${Theme.colors.red500}` : "0px solid transparent"};
  background-color: ${(props: CommentInputProps) =>
    props.disabled ? Theme.colors.charcoal050 : Theme.colors.white};
  cursor: ${(props: CommentInputProps) =>
    props.disabled ? "not-allowed" : "text"};
`;

const ACTION_TYPES = {
  CHANGE_EDITOR_VALUE: "CHANGE_EDITOR_VALUE",
  RESET_EDITOR: "RESET_EDITOR",
  EDITOR_ERROR: "EDITOR_ERROR",
} as const;

interface CommentEditorReducerState {
  text: string;
  editorState: EditorState;
  error: string;
}

const changeEditorValueAction = (val: string, next: any) => ({
  type: ACTION_TYPES.CHANGE_EDITOR_VALUE,
  payload: {
    val,
    next,
  },
});

const resetEditorAction = () => ({
  type: ACTION_TYPES.RESET_EDITOR,
});

const editorErrorAction = (error: string) => ({
  type: ACTION_TYPES.EDITOR_ERROR,
  error,
});

type CommentEditorReducerAction =
  | ReturnType<typeof changeEditorValueAction>
  | ReturnType<typeof resetEditorAction>
  | ReturnType<typeof editorErrorAction>;

const commentEditorInitialState: CommentEditorReducerState = {
  text: "",
  editorState: MentionTextarea.EMPTY_STATE,
  error: "",
};

const commentEditorReducer = (
  state: CommentEditorReducerState,
  action: CommentEditorReducerAction
): CommentEditorReducerState => {
  switch (action.type) {
    case ACTION_TYPES.CHANGE_EDITOR_VALUE:
      return {
        ...state,
        error: "",
        text: action.payload.val,
        editorState: action.payload.next,
      };
    case ACTION_TYPES.RESET_EDITOR:
      return commentEditorInitialState;
    case ACTION_TYPES.EDITOR_ERROR:
      return {
        ...state,
        error: action.error,
      };
    default:
      return state;
  }
};

const useCommentFlow = ({
  issueId,
  issueViewers,
}: {
  issueId: string;
  issueViewers: IssueViewers | undefined;
}) => {
  const [{ text, editorState, error }, editorDispatch] = useReducer(
    commentEditorReducer,
    commentEditorInitialState
  );
  const { T } = useTranslations();
  const dispatch = useDispatch();

  const handleCommentCancel = useCallback(() => {
    editorDispatch(resetEditorAction());
  }, []);

  const handleChange = useCallback(
    (val, next) => {
      editorDispatch(changeEditorValueAction(val, next));
    },
    [editorDispatch]
  );

  const filteredViewersData = useFilteredViewers(issueId, issueViewers);

  const { viewingUsers, viewingRoles, viewingCompanies } = filteredViewersData;

  const handleSubmit = useCallback(
    async (body: string) => {
      trackIssueAddComment({
        comment: body,
        users: viewingUsers,
        rolesList: viewingRoles,
        companiesList: viewingCompanies,
      });
      const createdComment = await dispatch(
        createIssueCommentAction(issueId, body)
      );
      if (createdComment) {
        editorDispatch(resetEditorAction());
      } else {
        editorDispatch(
          editorErrorAction(
            T.translate("issueEditForm.commentPostError") as string
          )
        );
      }
    },
    [T, dispatch, issueId, viewingCompanies, viewingRoles, viewingUsers]
  );

  return {
    text,
    editorState,
    error,
    handleCommentCancel,
    handleChange,
    handleSubmit,
  };
};

const useFilteredViewers = (
  issueId: string,
  issueViewers: IssueViewers | undefined
) => {
  const { data: users } = useUsersWrapper();
  const { data: rolesList } = useRolesWrapper();
  const { data: companiesList } = useCompaniesWrapper();

  const usersWithViewingPermission = useMemo(() => {
    if (!issueViewers) {
      return [];
    }

    return users.filter(
      (user: User) =>
        !!user.autodeskId &&
        issueViewers.usersOxygenIds.includes(user.autodeskId)
    );
  }, [issueViewers, users]);

  const companiesWithSomeViewers = useMemo(() => {
    if (!issueViewers) {
      return [];
    }

    return companiesList.filter((company: Company) => {
      if (!company.id) {
        return false;
      }
      return !!usersWithViewingPermission.find(
        (user: User) => user.companyId === company.id
      );
    });
  }, [companiesList, issueViewers, usersWithViewingPermission]);

  const rolesWithSomeViewers = useMemo(() => {
    if (!issueViewers) {
      return [];
    }

    return rolesList.filter((role: Role) => {
      if (!role.id) {
        return false;
      }
      return usersWithViewingPermission.find((user: User) =>
        user.roleIds.includes(role.id)
      );
    });
  }, [issueViewers, rolesList, usersWithViewingPermission]);

  const userViewersId = useMemo(() => {
    return usersWithViewingPermission.map((user: User) => user.autodeskId);
  }, [usersWithViewingPermission]);

  const roleViewersId = useMemo(() => {
    return rolesWithSomeViewers.map((role: Role) => role.memberGroupId);
  }, [rolesWithSomeViewers]);

  const companyViewersId = useMemo(() => {
    return companiesWithSomeViewers.map(
      (company: Company) => company.memberGroupId
    );
  }, [companiesWithSomeViewers]);
  return {
    viewingUsers: usersWithViewingPermission,
    viewingRoles: rolesWithSomeViewers,
    viewingCompanies: companiesWithSomeViewers,
    userViewersId,
    roleViewersId,
    companyViewersId,
  };
};

const useSuggestionFilter = ({
  issue,
  issueViewers,
  previousComments,
}: {
  issue: Issue;
  issueViewers: IssueViewers | undefined;
  previousComments: IssueComment[];
}) => {
  const { id: issueId, createdBy, assignedTo, assignedToType } = issue;
  const watcherProp = useWatcherPropName();
  const filteredViewersData = useFilteredViewers(issueId, issueViewers);

  const { userViewersId, roleViewersId, companyViewersId } =
    filteredViewersData;
  // using Set instead of Array to ease the load. These can be large arrays (2500+ users, and it causes lagging during typing if using arrays)
  const userViewersIdSet = useMemo(
    () => new Set(userViewersId),
    [userViewersId]
  );
  const roleViewersIdSet = useMemo(
    () => new Set(roleViewersId),
    [roleViewersId]
  );
  const companyViewersIdSet = useMemo(
    () => new Set(companyViewersId),
    [companyViewersId]
  );

  const sortUsersAlphabeticallyComparator = (a: User, b: User) => {
    if (!a?.firstName && !b?.firstName) {
      return 0;
    }
    if (!a?.firstName) {
      return -1;
    }
    if (!b?.firstName) {
      return 1;
    }
    return a.firstName.localeCompare(b.firstName);
  };

  const getSortedWatcherIds = useCallback(
    ({
      userData,
      watcherIds,
    }: {
      userData: User[];
      watcherIds: string[];
    }): string[] => {
      const watcherUsers = watcherIds.map((watcherId: string) =>
        userData.find((user: User) => user.autodeskId === watcherId)
      );
      // sort watchers alphabetically
      return (watcherUsers.filter(x => !!x) as User[])
        .sort(sortUsersAlphabeticallyComparator)
        .map(user => user.autodeskId);
    },
    []
  );

  const getSortedPreviousCommenterIds = useCallback(
    ({ userData }: { userData: User[] }): string[] => {
      const previousCommentersIds = previousComments
        .map((prevComm: IssueComment) => prevComm.createdBy)
        .filter(x => !!x);
      const previousCommenterUsers = previousCommentersIds
        .map((previousCommenterId: string) =>
          userData.find((user: User) => user.autodeskId === previousCommenterId)
        )
        .filter(x => !!x) as User[];
      return previousCommenterUsers
        .sort(sortUsersAlphabeticallyComparator)
        .map((user: User) => user.autodeskId);
    },
    [previousComments]
  );

  const filterUsers = useCallback(
    (data: User[]): User[] => {
      // first, constructing a list of priority members.
      // the priority list should be in this order:
      // the creator of the issue
      // the assignee of the issue
      // watchers (if any) - sorted alphabetically
      // previous commenters (if any) - sorted alphabetically (only for comments that were already loaded using the pagination of the comments)
      const userData = data as User[];
      let priorityIds = [createdBy];

      if (assignedToType === MENTION_TYPES.USER && assignedTo) {
        priorityIds = [...priorityIds, assignedTo];
      }

      if (issue[watcherProp]) {
        const watcherIds =
          watcherProp === "watchers"
            ? issue[watcherProp] || []
            : issue[watcherProp]?.map(x => x.id) || [];
        priorityIds = [
          ...priorityIds,
          ...getSortedWatcherIds({ userData, watcherIds }),
        ];
      }

      priorityIds = [
        ...priorityIds,
        ...getSortedPreviousCommenterIds({ userData }),
      ];

      const priorityMembers = priorityIds
        .map((pId: string) =>
          userData.find((user: User) => user.autodeskId === pId)
        )
        .filter(x => !!x) as User[];
      const nonPriorityMembers = defaultFilter(userData).filter((user: User) =>
        userViewersIdSet.has(user.autodeskId)
      );
      const membersToDisplay = [...priorityMembers, ...nonPriorityMembers];
      return uniqBy(membersToDisplay, "id");
    },
    [
      createdBy,
      assignedToType,
      assignedTo,
      issue,
      watcherProp,
      getSortedPreviousCommenterIds,
      getSortedWatcherIds,
      userViewersIdSet,
    ]
  );

  const filterRoles = useCallback(
    (data: Role[]): Role[] => {
      const roleData = data as Role[];
      let priorityIds: string[] = [];
      if (assignedToType === MENTION_TYPES.ROLE && assignedTo) {
        priorityIds = [...priorityIds, assignedTo];
      }
      const priorityRoles = priorityIds
        .map((pId: string) =>
          roleData.find((role: Role) => role.memberGroupId === pId)
        )
        .filter(x => !!x) as Role[];
      const nonPriorityRoles = defaultFilter(roleData).filter((role: Role) =>
        roleViewersIdSet.has(role.memberGroupId)
      );
      const rolesToDisplay = [...priorityRoles, ...nonPriorityRoles];
      return uniqBy(rolesToDisplay, "id");
    },
    [assignedTo, assignedToType, roleViewersIdSet]
  );

  const filterCompanies = useCallback(
    (data: Company[]): Company[] => {
      const companyData = data as Company[];
      let priorityIds: string[] = [];
      if (assignedToType === MENTION_TYPES.COMPANY && assignedTo) {
        priorityIds = [...priorityIds, assignedTo];
      }
      const priorityCompanies = priorityIds
        .map((pId: string) =>
          companyData.find((company: Company) => company.memberGroupId === pId)
        )
        .filter(x => !!x) as Company[];
      const nonPriorityCompanies = defaultFilter(companyData).filter(
        (company: Company) => companyViewersIdSet.has(company.memberGroupId)
      );
      const companiesToDisplay = [
        ...priorityCompanies,
        ...nonPriorityCompanies,
      ];
      return uniqBy(companiesToDisplay, "id");
    },
    [assignedTo, assignedToType, companyViewersIdSet]
  );

  const filter = useCallback(
    (data: any[], type: MentionType) => {
      if (type === MENTION_TYPES.USER) {
        return filterUsers(data);
      }
      if (type === MENTION_TYPES.ROLE) {
        return filterRoles(data);
      }
      if (type === MENTION_TYPES.COMPANY) {
        return filterCompanies(data);
      }
      return defaultFilter(data);
    },
    [filterCompanies, filterRoles, filterUsers]
  );
  return filter;
};

export const IssueCommentsPanelInput = ({
  issue,
  issueViewers,
  previousComments,
}: {
  issue: Issue;
  issueViewers: IssueViewers | undefined;
  previousComments: IssueComment[];
}) => {
  const issueId = issue.id;
  const isSendingComment = useSelector(getIsPostingIssueComment);
  const { user: alloyUser } = useUser();
  const { T } = useTranslations();
  const viewersMap = useSelector(getViewersMap);
  const doViewersExist =
    viewersMap &&
    viewersMap[issueId] &&
    viewersMap[issueId].status === FetchStatus.Success;

  // to be used when the FF is off or viewers didn't load, so a user won't be able to use mentions.
  const emptySuggestions: MentionData[] = [];

  const suggestionsFilter = useSuggestionFilter({
    issue,
    issueViewers,
    previousComments,
  });
  const { suggestions: alloySuggestions, onSearchChange } =
    useMentionSuggestions({ filter: suggestionsFilter });
  const suggestions = doViewersExist ? alloySuggestions : emptySuggestions;

  const {
    text,
    editorState,
    error,
    handleCommentCancel,
    handleChange,
    handleSubmit,
  } = useCommentFlow({ issueId, issueViewers });

  const {
    show,
    title,
    message,
    cancelButtonLabel,
    confirmationButtonLabel,
    isConfirmationButtonAlert,
    onConfirm,
    onClose,
    onSubmit,
  } = useMentionConfirmation(text, () => {
    handleSubmit(text);
  });

  const commentInputPlaceholder = useMemo(
    () => T.translate("issueEditForm.mentionInCommentPlaceholder") as string,
    [T]
  );

  return (
    <>
      <ModalConfirmation
        style={{}}
        show={show}
        title={title}
        message={message}
        isConfirmationButtonAlert={isConfirmationButtonAlert}
        cancelButtonLabel={cancelButtonLabel}
        confirmationButtonLabel={confirmationButtonLabel}
        onConfirm={onConfirm}
        onClose={onClose}
      />
      <CommentField
        style={{ marginTop: 15 }}
        user={{
          name: alloyUser!.name || "",
          imageUrl: alloyUser!.imageUrl,
        }}
        value={text}
        onSubmit={onSubmit}
        onCancel={handleCommentCancel}
        submitting={isSendingComment}
        error={error}
        renderInput={({ style, disabled, error, focused, ...props }) => (
          <Textarea disabled={disabled} error={error} focused={focused}>
            <MentionTextarea
              {...props}
              renderPopover={props => (
                <MentionPopover
                  {...props}
                  style={{
                    width: 275,
                  }}
                />
              )}
              suggestions={suggestions}
              onSearchChange={onSearchChange}
              editorState={editorState}
              onChange={handleChange}
              placeholder={commentInputPlaceholder}
              style={{
                ...style,
                margin: 0,
                padding: "5px",
                minHeight: "76px",
                width: "100%",
                whiteSpace: "pre-wrap",
                overflowWrap: "break-word",
                wordBreak: "break-word",
              }}
            />
          </Textarea>
        )}
      />
    </>
  );
};
